<template>
  <v-row class="fill-height" no-gutters>
    <v-col class="d-flex flex-column">
      <div>
        <navigation-header class="px-0" title="Szkolenia i testy"></navigation-header>
      </div>
      <div class="flex-grow-1 d-flex" :class="!loading && showContent ? 'align-start justify-start' : 'align-center justify-center'">
        <fp-loader v-if="loading" circle size="120px" />
        <div v-else class="pb-16 d-flex gap-4 ma-3">
          <template v-if="showContent">
            <div v-if="AuthService.User?.quiz?.requireEthic || !!lastPositiveAttempts[QuizCategory.ETHIC]">
              <quiz-card :last-positive-attempt="lastPositiveAttempts[QuizCategory.ETHIC] ?? undefined"
                :category="QuizCategory.ETHIC" />
            </div>
            <div v-if="AuthService.User?.quiz?.requireKnf || !!lastPositiveAttempts[QuizCategory.KNF]">
              <quiz-card :last-positive-attempt="lastPositiveAttempts[QuizCategory.KNF] ?? undefined"
                :category="QuizCategory.KNF" />
            </div>
          </template>
          <div v-else class="text-center d-flex flex-column justify-center align-center gap-3">
            <LendaIcon :src="require('@/assets/icons/quizes/no_statements.svg')" width="99" height="62" />
            <span class="headline-3">
              Nie masz żadnych szkoleń
              <br />
              do zaliczenia
            </span>
          </div>
        </div>
      </div>
    </v-col>
  </v-row>
</template>
<script setup lang="ts">
import HumanResourcesApi from '@/modules/adminPanel/services/HumanResourcesApi';
import { QuizTest, QuizTestAttempt } from '@/modules/bhp/models/QuizTest';
import { onMounted, ref, computed } from 'vue';
import { QuizCategory } from '@/models/Quiz';
import QuizCard from '@/modules/Statements/components/QuizCard.vue';
import AuthService from '@/modules/user/AuthService';
import QuizCardSuccess from '@/modules/Statements/components/QuizCardSuccess.vue';
import { useInstance } from '@/composables/useInstance';
import { useModal } from '@/components/L/Modal/useModal';
import NavigationHeader from '@/components/NavigationHeader/NavigationHeader.vue';
import LendaIcon from '@/components/commons/LendaIcon.vue';

const loading = ref<boolean>(false);
const quizzes = ref<QuizTest[]>([]);
const lastPositiveAttempts = ref<Record<QuizCategory, Nullable<QuizTestAttempt>>>({
  [QuizCategory.KNF]: null,
  [QuizCategory.OSH]: null,
  [QuizCategory.STMT]: null,
  [QuizCategory.ETHIC]: null,
  [QuizCategory.BANK_STMTS]: null,
});
const categories = ref<QuizCategory[]>([QuizCategory.ETHIC, QuizCategory.KNF]);
const { $route, $router } = useInstance();
const { openModal, } = useModal();

const showContent = computed(() => {
  return (AuthService.User?.quiz?.requireEthic || !!lastPositiveAttempts.value[QuizCategory.ETHIC]) || (AuthService.User?.quiz?.requireKnf || !!lastPositiveAttempts.value[QuizCategory.KNF])
})

const getLatestResultForCurrentUser = async () => {
  try {
    loading.value = true;
    const results = await Promise.allSettled(categories.value.map(async (category) => {
      return await HumanResourcesApi.getLatestResultForCurrentUser(category);
    }));

    categories.value.forEach((category, index) => {
      const result = results[index];
      if (result.status === 'fulfilled') {
        lastPositiveAttempts.value[category] = result.value;
      } else if (result.reason?.response?.status !== 404) {
        // Only log non-404 errors
        console.error(`Error fetching results for ${category}:`, result.reason);
      }
    });
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false;
  }
}

const getNewestQuizTestQuestions = async () => {
  try {
    const ethicQuiz = await HumanResourcesApi.getNewestQuizTestQuestions(QuizCategory.ETHIC);
    const knfQuiz = await HumanResourcesApi.getNewestQuizTestQuestions(QuizCategory.KNF);
    quizzes.value = ethicQuiz || knfQuiz ? [ethicQuiz, knfQuiz] : [];
  } catch (e) {
    console.error(e);
  }
};


onMounted(async () => {
  await getLatestResultForCurrentUser();
  await getNewestQuizTestQuestions();

  if ($route.value?.query.quizSuccess) {
    const category = $route.value?.query.category as QuizCategory;
    $router.value?.push({ query: { quizSuccess: undefined, }, })
    await openModal(QuizCardSuccess, {
      lastPositiveAttempt: lastPositiveAttempts.value[category]!,
      quizCategory: category
    },)
  }
});
</script>

